import { decodeTokenPayload } from './tokenPayload';
import { tokenService } from './tokenService';

/**
 * The permission codes the server granted this session.
 *
 * <h2>Why not `core/security/permissions.ts`</h2>
 *
 * That module maps an app role slug to a fixed list of made-up codes
 * (`workflow:manage` and the rest). It answers "what does an admin usually get",
 * which is a useful default for hiding a menu item and no use at all for a
 * screen whose access is granted independently of the role: the platform's own
 * RBAC lists Workflow Configuration as a function with its own row, so a caller
 * can hold `WORKFLOW_VIEW` without being an administrator and can be an
 * administrator without holding it.
 *
 * These are the **server's** codes -- `WORKFLOW_VIEW`, `SETTINGS_UPDATE`,
 * `ROLE_VIEW` -- taken from the `perms` claim the access token carries, which is
 * exactly the list `@perm.has(...)` is evaluated against on the other end. So
 * what the UI offers and what the API will accept cannot disagree.
 *
 * <h2>This is not the protection</h2>
 *
 * A token is decoded here, not verified -- the client holds no key and could
 * not verify one if it did. Every endpoint enforces the same codes server-side
 * and refuses the request whatever this says. The job here is only to avoid
 * offering an action that is going to be refused.
 */

function claimList(payload: unknown, claim: string): readonly string[] {
  if (typeof payload !== 'object' || payload === null) return [];
  const value = (payload as Record<string, unknown>)[claim];
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string');
}

/**
 * Every code in the current access token, or an empty list.
 *
 * Empty covers a missing token, an unreadable one and a token with no `perms`
 * claim. All three mean "nothing is known to be granted", which is the safe
 * reading: a screen gated on a code stays closed rather than opening on a
 * decode that failed.
 */
export function serverPermissions(): readonly string[] {
  return claimList(decodeTokenPayload(tokenService.getAccessToken()), 'perms');
}

/** Whether the session holds a given server permission code. */
export function hasServerPermission(code: string): boolean {
  return serverPermissions().includes(code);
}
